import { Badge, Progress } from '@zoom/components';
import classNames from 'classnames';
import { differenceInSeconds, format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import React, { memo } from 'react';
import { Flipped } from 'react-flip-toolkit';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../app/AuthorizationProvider';
import { EllipsisContent } from '../../components/EllipsisContent';
import { ViewIcon } from '../../components/ListIcons/ViewIcon';
import { translateScheduleElementName } from '../../components/MySchedule/transformData';
import { SortedBulkLabels } from '../../components/SortedBulkLabels';
import { RealTimeAdherenceState, ScheduleElement, Team } from '../../graphql/components';
import { formatNameOfPerson, nameOf } from '../../utils/helpers';
import { OutOfAdherenceDuration } from './OutOfAdherenceDuration';
import { getListItemTag, TRANSITION_DELAY_TIMEOUT } from './constants';
import { AgentOverviewWithTeams, AgentTransition } from './types';

export type RealTimeAdherenceRowComponentProps = {
  agentOverview: AgentOverviewWithTeams;
  index: number;
  isDetailView: boolean;
  onDetailView: (agentId: string) => void;
  sceneTransition?: AgentTransition;
  timeStamp: number;
  teams: Record<string, Team>;
};

export const RealTimeAdherenceRowComponent: React.FC<RealTimeAdherenceRowComponentProps> = ({
  agentOverview: { agentId, currentActivity, expectedActivity, firstName, inStateSinceTime, lastName, state, teamIds },
  index,
  isDetailView,
  onDetailView,
  sceneTransition: { type: transitionType, timeStamp: transitionTimeStamp } = {},
  timeStamp,
  teams,
}) => {
  const { localeObj, timezone } = useAuth();
  const { t } = useTranslation();

  const isInAdherence = state === RealTimeAdherenceState.In;
  const fullDateFormat = t('date.formats.full');

  return (
    <Flipped key={agentId} flipId={agentId}>
      <tr
        className={classNames({
          'table-active': isDetailView,
          'in-transition': transitionType === 'start',
          'after-transition': transitionType === 'end',
          'out-of-adherence': !isInAdherence,
        })}
      >
        <EllipsisContent as="td" data-test-id={getListItemTag(index, 'name')} className="font-weight-bold">
          {formatNameOfPerson(firstName, lastName)}
        </EllipsisContent>
        <td className="col-wrap text-center" data-test-id={getListItemTag(index, 'teamIds')}>
          <SortedBulkLabels
            items={teamIds?.map((teamId) => teams[teamId]).filter(Boolean)}
            label={t('team.team', { count: 2 })}
          />
        </td>
        <td
          className="text-center"
          data-test-id={getListItemTag(index, nameOf<AgentOverviewWithTeams>('currentActivity'))}
        >
          {currentActivity}
        </td>
        <td
          data-test-id={getListItemTag(index, nameOf<AgentOverviewWithTeams>('expectedActivity'))}
          className="text-wrap text-center"
        >
          {translateScheduleElementName(t, {
            name: expectedActivity,
          } as ScheduleElement)}
        </td>
        <td
          data-test-id={getListItemTag(index, nameOf<AgentOverviewWithTeams>('state'))}
          className="text-wrap text-center text-uppercase"
        >
          <Badge kind={isInAdherence ? 'secondary' : 'danger'}>
            {t(`layout.confirm.${isInAdherence ? 'yes' : 'no'}`)}
          </Badge>
          {transitionType === 'start' && transitionTimeStamp ? (
            <Progress
              data-test-id={getListItemTag(index, 'time-until-resort')}
              value={(differenceInSeconds(transitionTimeStamp, timeStamp) / TRANSITION_DELAY_TIMEOUT) * 1000 * 100}
            />
          ) : null}
        </td>
        <td
          data-test-id={getListItemTag(index, nameOf<AgentOverviewWithTeams>('inStateSinceTime'))}
          className="text-wrap text-center font-weight-bold"
        >
          {isInAdherence
            ? '--'
            : format(utcToZonedTime(inStateSinceTime, timezone), fullDateFormat, {
                locale: localeObj,
              })}
        </td>
        <td
          data-test-id={getListItemTag(index, 'timeOutOfAdherence')}
          className="text-wrap text-center font-weight-bold"
        >
          {isInAdherence ? '--' : <OutOfAdherenceDuration ooaSinceValue={inStateSinceTime} timeStamp={timeStamp} />}
        </td>
        <td className="list-actions">
          <ViewIcon
            onClick={() => onDetailView(agentId)}
            testId={getListItemTag(index, 'view')}
            tooltipMessageKey={t('layout.action.view')}
          />
        </td>
      </tr>
    </Flipped>
  );
};

export const RealTimeAdherenceRow = memo(RealTimeAdherenceRowComponent);
